import { useCallback, useContext } from 'react';

import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { isForbiddenError } from '@vyce/core/src/utils/permissions';

/**
 * Error handler for a widget's own data request.
 *
 * A 403 is the server telling us this widget isn't for this user — the client's
 * permission table missed it, or a right was renamed on the backend. Reporting it
 * as an error would put a red "No access" toast on the dashboard for every such
 * widget, so instead the widget is hidden and nothing is shown. Everything else
 * is a real failure and still goes through handleServerError.
 *
 * Use it for the widget's main fetch only. Row actions (remove, export) must keep
 * telling the user when they fail.
 */
export const useWidgetErrorHandler = (onForbidden?: (() => void) | null) => {
  const { handleServerError } = useContext(NotificationContext);

  return useCallback(
    (error: any) => {
      if (isForbiddenError(error) && onForbidden) {
        onForbidden();
        return;
      }
      handleServerError(error);
    },
    [handleServerError, onForbidden]
  );
};
